import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LABELS, type CyclePhase, type DdBasis } from "@match/shared";
import { PageHeader } from "@/components/Layout";
import { PairView } from "@/components/pair";
import { PHASE_LABEL } from "@/components/session";
import { Button, ErrorNote, Field, inputClass, useCanWrite } from "@/components/ui";
import { api, errorMessage } from "@/lib/api";
import { money, percent } from "@/lib/format";
import { useAccountGroups, useCycles, usePropFirms, useRefreshDirectory, useServers, useSessions } from "@/lib/queries";
import { decimalInput, fractionToPct, pctToFraction } from "@/lib/units";
import type { Account, AccountGroup, BrokerServer, CycleListItem, Preset, SessionListItem } from "@/lib/types";

const PHASES: CyclePhase[] = ["PHASE_1", "PHASE_2", "FUNDED"];
const BASES = Object.keys(LABELS.ddBasis) as DdBasis[];

/**
 * La Fase viva che tiene un conto, scritta per il menu. Un conto sta in una
 * Fase sola: quando c'e', il conto non si puo' scegliere.
 */
const occupatoDa = (a: Account) =>
  !a.liveSession ? null : a.liveSession.cycleLabel ? `${PHASE_LABEL(a.liveSession.phase)} · ${a.liveSession.cycleLabel}` : PHASE_LABEL(a.liveSession.phase);

/**
 * LA FASE VIVA CHE TIENE UN CICLO, per ciclo. Un ciclo ne regge una sola: due
 * partirebbero dallo stesso recover e lo conterebbero due volte. Come per i
 * conti occupati, il menu lo dice e lo spegne invece di lasciar scegliere e
 * farsi rifiutare dopo — il rifiuto del backend resta comunque, perche' fra il
 * caricamento dell'elenco e la conferma qualcuno puo' averne aperta una.
 */
const faseVivaPerCiclo = (sessions: SessionListItem[]) =>
  new Map(sessions.map((x) => [x.cycleId, `${PHASE_LABEL(x.phase)} · ${LABELS.sessionState[x.state]}`] as const));

/**
 * I CICLI A CUI QUESTA FASE SI PUO' ATTACCARE, dal 22/09: quelli attivi del
 * cliente che stanno gia' nella fase richiesta. La fase 1 non ne ha nessuno —
 * ne apre sempre uno NUOVO, perche' una challenge nuova ha fee sua e recover
 * suo — e per le altre due sceglie una persona: con piu' cicli attivi per
 * cliente «il piu' recente» e «quello giusto» non sono piu' la stessa riga.
 */
const cicliPer = (cycles: CycleListItem[], clientId: string, phase: CyclePhase) =>
  phase === "PHASE_1" ? [] : cycles.filter((c) => c.client.id === clientId && c.currentPhase === phase);

/** L'etichetta di un ciclo nel menu: chi è, cosa ha pagato, cosa deve recuperare. */
const cicloLabel = (c: CycleListItem) =>
  `${c.label ?? "senza etichetta"} · ${c.propFirm.name} ${c.preset.name} · fee ${money(c.feesPaid, c.currency)} · ${LABELS.terms.recoverAmount} ${money(c.recoverAmount, c.currency)}`;

/**
 * Che cosa dire sotto il menu dei cicli: quando c'è da scegliere, e quando non
 * c'è piu' niente da scegliere perche' sono tutti impegnati.
 */
const cicloHint = (candidati: CycleListItem[], occupati: Map<string, string>) => {
  const liberi = candidati.filter((c) => !occupati.has(c.id)).length;
  if (candidati.length && !liberi) return "Tutti hanno già una Fase viva: va chiusa quella che li tiene — la pausa non libera il ciclo.";
  if (liberi > 1) return "Il cliente ne ha più d'uno in questa fase: scegli quello giusto, i numeri della fase vengono da lì.";
  return undefined;
};

/** Il conto preferito se e' libero, altrimenti il primo libero, altrimenti nessuno. */
const primoLibero = (list: Account[], preferito: string | undefined) =>
  list.find((a) => a.id === preferito && !a.liveSession)?.id ?? list.find((a) => !a.liveSession)?.id ?? "";

const targetFor = (preset: Preset | undefined, phase: CyclePhase) =>
  !preset
    ? ""
    : phase === "PHASE_1"
      ? fractionToPct(preset.phase1TargetPct)
      : phase === "PHASE_2"
        ? fractionToPct(preset.phase2TargetPct)
        : fractionToPct(preset.fundedTargetPct);

/**
 * Creazione di una Fase: UN form, non un wizard.
 *
 * La Fase tiene la coppia di conti, il simbolo, il ciclo e i limiti di rischio.
 * Lotto, moltiplicatore, stop loss e take profit NON stanno qui: si scelgono a
 * ogni operazione, nella scheda Operazione. Senza quei quattro campi non
 * restava abbastanza per tre schermate — e non c'e' piu' niente da proiettare
 * qui dentro, perche' la proiezione della bruciatura ha bisogno proprio di
 * quelli.
 */
export function SessionWizard() {
  const canWrite = useCanWrite();
  const navigate = useNavigate();
  const refresh = useRefreshDirectory();
  const groups = useAccountGroups();
  const servers = useServers();
  const firms = usePropFirms();
  // Tutti i cicli attivi del tenant: il menu «si attacca a» pesca da qui.
  const cycles = useCycles({ state: ["ACTIVE"] });
  // E le Fasi vive, per sapere quali di quei cicli sono già impegnati.
  const vive = useSessions({ state: ["ACTIVE", "PAUSED"] });

  // --- Coppia, fase, simbolo ---
  const [clientId, setClientId] = useState("");
  const [propAccountId, setPropAccountId] = useState("");
  const [brokerAccountId, setBrokerAccountId] = useState("");
  const [phase, setPhase] = useState<CyclePhase>("PHASE_1");
  const [cycleId, setCycleId] = useState("");
  const [cycleLabel, setCycleLabel] = useState("");
  const [symbol, setSymbol] = useState("");

  // --- Ciclo, solo se nuovo ---
  const [presetId, setPresetId] = useState("");
  const [accountSize, setAccountSize] = useState("");
  const [fee, setFee] = useState("");
  const [feeCurrency, setFeeCurrency] = useState("");
  const [feeRate, setFeeRate] = useState("");
  const [maxDdPct, setMaxDdPct] = useState("");
  const [basis, setBasis] = useState<DdBasis>("STATIC_INITIAL");

  // --- Limiti della fase ---
  const [dailyDdPct, setDailyDdPct] = useState("");
  const [targetPct, setTargetPct] = useState("");

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const group = groups.data?.find((g) => g.client.id === clientId);
  // La fase 1 apre un ciclo nuovo; le altre due si attaccano a uno esistente.
  const nuovoCiclo = phase === "PHASE_1";
  const candidati = cicliPer(cycles.data ?? [], clientId, phase);
  const occupati = faseVivaPerCiclo(vive.data ?? []);
  const liberi = candidati.filter((c) => !occupati.has(c.id));
  const cycle = candidati.find((c) => c.id === cycleId) ?? null;
  const propAccounts = group?.accounts.filter((a) => a.role === "PROP") ?? [];
  const brokerAccounts = group?.accounts.filter((a) => a.role === "BROKER") ?? [];
  const prop = propAccounts.find((a) => a.id === propAccountId) ?? null;
  const broker = brokerAccounts.find((a) => a.id === brokerAccountId) ?? null;
  const allPresets = (firms.data ?? []).flatMap((f) => f.presets.map((p) => ({ ...p, firmName: f.name })));
  const preset = allPresets.find((p) => p.id === (cycle ? cycle.preset.id : presetId));

  // Scelto il cliente: primi conti LIBERI, e il menu Fase torna a Fase 1.
  useEffect(() => {
    if (!group) return;
    // Il primo LIBERO, non il primo: la coppia del gruppo e' quella della Fase
    // viva, cioe' esattamente quella che non si puo' riusare.
    setPropAccountId(primoLibero(propAccounts, group.pair.prop?.id));
    setBrokerAccountId(primoLibero(brokerAccounts, group.pair.broker?.id));
    setSymbol("");
    /*
     * LA FASE RIPARTE SEMPRE DA FASE 1.
     *
     * Prima si posizionava sul ciclo attivo del cliente, ed era un predefinito
     * onesto finche' il ciclo era uno. Con piu' cicli aperti quello del gruppo
     * e' solo il piu' recente: partire da li' vuol dire proporre la fase di un
     * ciclo che magari non c'entra. Il caso normale e' una challenge nuova.
     */
    setPhase("PHASE_1");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  /*
   * IL CICLO SI PRESELEZIONA SOLO QUANDO NON C'E' DA SCEGLIERE: uno solo
   * LIBERO, e il menu lo propone. Due, e resta vuoto — indovinare qui e' lo
   * stesso errore che il backend ha smesso di fare. Uno con una Fase viva non
   * si propone: sarebbe una proposta che non si puo' confermare. Una scelta
   * che non è piu' fra i candidati (cambiata fase o cliente, o Fase aperta
   * nel frattempo) si butta: non si porta dietro un id.
   */
  const idsLiberi = liberi.map((c) => c.id).join(",");
  useEffect(() => {
    if (nuovoCiclo) return setCycleId("");
    const unico = liberi.length === 1 ? liberi[0] : undefined;
    setCycleId(unico?.id ?? (liberi.some((c) => c.id === cycleId) ? cycleId : ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsLiberi, nuovoCiclo]);

  // Scelto il preset (o trovato il ciclo): si precompila.
  useEffect(() => {
    if (!preset) return;
    if (!cycle) {
      setAccountSize(String(Number(preset.accountSize)));
      setFee(String(Number(preset.challengeFee)));
      setMaxDdPct(fractionToPct(preset.maxDdPct));
      setBasis(preset.maxDdBasis);
    }
    setDailyDdPct(fractionToPct(preset.dailyDdPct));
    setTargetPct(targetFor(preset, phase));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset?.id, cycle?.id]);

  useEffect(() => {
    if (preset) setTargetPct(targetFor(preset, phase));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // Valori effettivi: con un ciclo attivo valgono i suoi.
  const effective = {
    accountSize: cycle ? String(Number(cycle.accountSize)) : decimalInput(accountSize),
    maxDdPct: cycle ? cycle.maxDdPct : pctToFraction(maxDdPct),
    basis: cycle ? cycle.maxDdBasis : basis,
    feesPaid: cycle ? String(Number(cycle.feesPaid)) : decimalInput(fee),
  };

  const problems = [
    !prop && "conto prop",
    !broker && "conto broker",
    prop?.liveSession && "un conto prop libero",
    broker?.liveSession && "un conto broker libero",
    !symbol && "simbolo",
    !nuovoCiclo && !cycle && "ciclo a cui attaccare la fase",
    nuovoCiclo && !presetId && "preset della prop firm",
    nuovoCiclo && !effective.accountSize && "taglia del conto",
    nuovoCiclo && !effective.maxDdPct && "max drawdown",
    nuovoCiclo && !effective.feesPaid && "fee",
    dailyDdPct.trim() && !pctToFraction(dailyDdPct) && "DD giornaliero",
    targetPct.trim() && !pctToFraction(targetPct) && "target di fase",
  ].filter(Boolean) as string[];

  const create = async () => {
    if (problems.length || !prop || !broker) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.sessions.create({
        clientId,
        propAccountId: prop.id,
        brokerAccountId: broker.id,
        canonicalSymbol: symbol,
        phase,
        ...(cycle && { cycleId: cycle.id }),
        ...(nuovoCiclo && {
          presetId,
          cycleLabel: cycleLabel.trim() || null,
          accountSize: effective.accountSize!,
          challengeFee: effective.feesPaid!,
          challengeFeeCurrency: feeCurrency || prop.currency,
          ...(decimalInput(feeRate) ? { challengeFeeFxRate: decimalInput(feeRate)! } : {}),
          maxDdPct: effective.maxDdPct!,
          maxDdBasis: effective.basis,
        }),
        dailyDdPct: dailyDdPct.trim() ? pctToFraction(dailyDdPct) : null,
        challengeTargetPct: targetPct.trim() ? pctToFraction(targetPct) : null,
      });
      await refresh();
      navigate(`/fasi/${r.session.id}`);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (!canWrite) {
    return (
      <>
        <PageHeader title="Nuova fase" />
        <div className="px-6 py-6">
          <ErrorNote>Il tuo ruolo non può creare fasi.</ErrorNote>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Nuova fase" subtitle={group ? group.client.name : "Coppia di conti, ciclo, limiti"} />

      <div className="max-w-5xl space-y-6 px-6 py-5">
        <ClientSection
          groups={groups.data ?? []}
          servers={servers.data ?? []}
          clientId={clientId}
          setClientId={setClientId}
          group={group}
          propAccountId={propAccountId}
          setPropAccountId={setPropAccountId}
          brokerAccountId={brokerAccountId}
          setBrokerAccountId={setBrokerAccountId}
          phase={phase}
          setPhase={setPhase}
          candidati={candidati}
          occupati={occupati}
          cycleId={cycleId}
          setCycleId={setCycleId}
          cycleLabel={cycleLabel}
          setCycleLabel={setCycleLabel}
          symbol={symbol}
          setSymbol={setSymbol}
        />

        {group && prop && broker && (
          <div className="grid gap-6 lg:grid-cols-2">
            <section className="space-y-4">
              <h2 className="text-sm font-medium text-ink">{nuovoCiclo ? "Prop firm e ciclo" : "Ciclo scelto"}</h2>
              {!nuovoCiclo && !cycle ? (
                <p className="rounded-card border border-line bg-surface p-4 text-sm text-muted">Scegli il ciclo qui sopra: i suoi numeri compaiono qui.</p>
              ) : cycle ? (
                <dl className="grid grid-cols-2 gap-3 rounded-card border border-line bg-surface p-4 text-sm">
                  <Info label="Preset">{`${cycle.propFirm.name} ${cycle.preset.name}`}</Info>
                  <Info label="Fase">{PHASE_LABEL(cycle.currentPhase)}</Info>
                  <Info label="Taglia">{money(cycle.accountSize, prop.currency)}</Info>
                  <Info label="Max DD">{`${percent(cycle.maxDdPct)} · ${LABELS.ddBasis[cycle.maxDdBasis]}`}</Info>
                  <Info label="Fee pagate">{money(cycle.feesPaid, cycle.currency)}</Info>
                  <Info label={LABELS.terms.recoverAmount}>{money(cycle.recoverAmount, cycle.currency)}</Info>
                  <p className="col-span-2 text-xs text-faint">I parametri del ciclo sono fissati all'apertura: la fase li riusa.</p>
                </dl>
              ) : (
                <>
                  <Field label="Preset">
                    {(id) => (
                      <select id={id} value={presetId} onChange={(e) => setPresetId(e.target.value)} className={inputClass}>
                        <option value="" disabled>
                          Scegli prop firm e modello
                        </option>
                        {(firms.data ?? []).map((f) => (
                          <optgroup key={f.id} label={f.name}>
                            {f.presets.map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.name} · {p.phases === 0 ? "instant" : p.phases === 1 ? "one-step" : "two-step"}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    )}
                  </Field>
                  <div className="grid grid-cols-2 gap-3">
                    <NumField label="Taglia del conto" value={accountSize} onChange={setAccountSize} suffix={prop.currency} />
                    <div className="grid grid-cols-3 gap-2">
                      <div className="col-span-2">
                        <NumField label="Fee della challenge" value={fee} onChange={setFee} hint="Entra nel registro del ciclo, convertita nella valuta di reporting." />
                      </div>
                      <Field label="Valuta">
                        {(id) => <input id={id} value={feeCurrency || prop.currency} maxLength={3} onChange={(e) => setFeeCurrency(e.target.value.toUpperCase())} className={inputClass} />}
                      </Field>
                    </div>
                    <NumField label="Max drawdown" value={maxDdPct} onChange={setMaxDdPct} suffix="%" />
                    <Field label="Base di calcolo del DD">
                      {(id) => (
                        <select id={id} value={basis} onChange={(e) => setBasis(e.target.value as DdBasis)} className={inputClass}>
                          {BASES.map((b) => (
                            <option key={b} value={b}>
                              {LABELS.ddBasis[b]}
                            </option>
                          ))}
                        </select>
                      )}
                    </Field>
                  </div>
                </>
              )}
            </section>

            <section className="space-y-4">
              <h2 className="text-sm font-medium text-ink">Limiti della fase</h2>
              <p className="text-sm text-muted">
                Valgono su tutte le operazioni. Lotto, moltiplicatore, stop loss e take profit non stanno qui: si scelgono a ogni operazione.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <NumField label="DD giornaliero" value={dailyDdPct} onChange={setDailyDdPct} suffix="%" hint="Vuoto se la prop non lo prevede." />
                <NumField label={`Target ${PHASE_LABEL(phase)}`} value={targetPct} onChange={setTargetPct} suffix="%" hint="Vuoto in funded." />
              </div>
            </section>
          </div>
        )}

        <ErrorNote>{error}</ErrorNote>
        {/Tasso .* non disponibile/.test(error ?? "") && (
          <div className="max-w-xs">
            <Field label="Tasso della fee" hint="Né feed del broker né BCE disponibili: scrivi il tasso da congelare sulla fee, poi crea di nuovo.">
              {(id) => <input id={id} inputMode="decimal" value={feeRate} placeholder="es. 0.9200" onChange={(e) => setFeeRate(e.target.value)} className={inputClass} />}
            </Field>
          </div>
        )}

        <div className="flex items-center justify-between border-t border-line pt-4">
          <Button variant="ghost" onClick={() => navigate("/fasi")}>
            Annulla
          </Button>
          <div className="flex items-center gap-3">
            {problems.length > 0 && <span className="text-xs text-muted">Manca: {problems.join(", ")}</span>}
            <Button variant="primary" disabled={busy || problems.length > 0} onClick={() => void create()}>
              {busy ? "Creazione…" : "Crea fase"}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------

function ClientSection(props: {
  groups: AccountGroup[];
  servers: BrokerServer[];
  clientId: string;
  setClientId: (v: string) => void;
  group: AccountGroup | undefined;
  propAccountId: string;
  setPropAccountId: (v: string) => void;
  brokerAccountId: string;
  setBrokerAccountId: (v: string) => void;
  phase: CyclePhase;
  setPhase: (v: CyclePhase) => void;
  candidati: CycleListItem[];
  /** Per ciclo, la Fase viva che lo tiene: c'è = non si può scegliere. */
  occupati: Map<string, string>;
  cycleId: string;
  setCycleId: (v: string) => void;
  cycleLabel: string;
  setCycleLabel: (v: string) => void;
  symbol: string;
  setSymbol: (v: string) => void;
}) {
  const { groups, servers, group } = props;
  const reason = (g: AccountGroup) => {
    const hasProp = g.accounts.some((a) => a.role === "PROP");
    const hasBroker = g.accounts.some((a) => a.role === "BROKER");
    if (!hasProp && !hasBroker) return "senza conti";
    if (!hasProp) return "manca il conto prop";
    if (!hasBroker) return "manca il conto broker";
    // Una Fase gia' aperta NON spegne piu' il cliente: dal 21/09 se ne aprono
    // quante se ne vogliono. Quello che si spegne e' il CONTO occupato, qui
    // sotto, perche' e' li' che sta il vincolo.
    return null;
  };

  const prop = group?.accounts.find((a) => a.id === props.propAccountId) ?? null;
  const broker = group?.accounts.find((a) => a.id === props.brokerAccountId) ?? null;
  const propServer = servers.find((s) => s.id === prop?.serverId);
  const brokerServer = servers.find((s) => s.id === broker?.serverId);

  const symbolState = useMemo(() => {
    if (!propServer || !brokerServer) return null;
    const onProp = new Set(propServer.symbols.map((s) => s.canonical));
    const onBroker = new Set(brokerServer.symbols.map((s) => s.canonical));
    return {
      both: [...onProp].filter((c) => onBroker.has(c)).sort(),
      onlyProp: [...onProp].filter((c) => !onBroker.has(c)).sort(),
      onlyBroker: [...onBroker].filter((c) => !onProp.has(c)).sort(),
    };
  }, [propServer, brokerServer]);

  const serverName = (s: BrokerServer) => s.displayName ?? s.name;
  const link = (s: BrokerServer, canonical?: string) => `/server?server=${s.id}${canonical ? `&symbol=${encodeURIComponent(canonical)}` : ""}`;

  return (
    <div className="space-y-5">
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Cliente" hint={groups.length === 0 ? "Nessun cliente: creane uno dalla pagina Clienti." : undefined}>
          {(id) => (
            <select id={id} value={props.clientId} onChange={(e) => props.setClientId(e.target.value)} className={inputClass}>
              <option value="" disabled>
                Scegli il cliente
              </option>
              {groups.map((g) => {
                const why = reason(g);
                return (
                  <option key={g.client.id} value={g.client.id} disabled={!!why}>
                    {g.client.name}
                    {why ? ` · ${why}` : g.cycle ? ` · ciclo in ${PHASE_LABEL(g.cycle.currentPhase)}` : ""}
                  </option>
                );
              })}
            </select>
          )}
        </Field>
        <Field
          label="Fase"
          hint={
            props.phase === "PHASE_1"
              ? "La fase 1 apre sempre un ciclo nuovo, con la sua fee e il suo recover."
              : "Continua un ciclo già aperto: quale, si sceglie qui sotto."
          }
        >
          {(id) => (
            <select id={id} value={props.phase} onChange={(e) => props.setPhase(e.target.value as CyclePhase)} className={inputClass}>
              {PHASES.map((p) => (
                <option key={p} value={p}>
                  {PHASE_LABEL(p)}
                </option>
              ))}
            </select>
          )}
        </Field>
      </div>

      {/*
        * IL CICLO A CUI LA FASE SI ATTACCA, e i numeri con cui partirà.
        *
        * Compare solo in fase 2 e funded: la fase 1 il ciclo lo apre, e non ha
        * niente da scegliere. Senza candidati non si lascia un menu vuoto: si
        * dice che cosa manca e come si arriva ad averne uno.
        */}
      {props.clientId && props.phase !== "PHASE_1" && (
        <Field label="Ciclo" hint={cicloHint(props.candidati, props.occupati)}>
          {(id) =>
            props.candidati.length ? (
              <select id={id} value={props.cycleId} onChange={(e) => props.setCycleId(e.target.value)} className={inputClass}>
                <option value="" disabled>
                  Scegli il ciclo
                </option>
                {props.candidati.map((c) => {
                  const occupato = props.occupati.get(c.id);
                  return (
                    <option key={c.id} value={c.id} disabled={!!occupato}>
                      {cicloLabel(c)}
                      {occupato ? ` · ha già una Fase viva: ${occupato}` : ""}
                    </option>
                  );
                })}
              </select>
            ) : (
              <p id={id} className="rounded-card border border-line bg-surface p-3 text-sm text-muted">
                Il cliente non ha cicli attivi in {PHASE_LABEL(props.phase)}: un ciclo ci arriva superando la fase precedente. Per una challenge nuova scegli Fase 1.
              </p>
            )
          }
        </Field>
      )}

      {group && (
        <>
          {/*
            * I due menu stanno SEMPRE a schermo, anche con un conto solo per
            * ruolo: da quando un cliente apre piu' Fasi, l'unico conto prop
            * puo' essere occupato, e un menu che non c'e' non lo direbbe.
            */}
          <div className="grid gap-4 md:grid-cols-2">
            {([
              ["Conto prop", "PROP", props.propAccountId, props.setPropAccountId],
              ["Conto broker", "BROKER", props.brokerAccountId, props.setBrokerAccountId],
            ] as const).map(([label, role, value, set]) => {
              const conti = group.accounts.filter((a) => a.role === role);
              const liberi = conti.filter((a) => !a.liveSession).length;
              return (
                <Field key={role} label={label} hint={conti.length && !liberi ? "Tutti in una fase viva: chiudi o metti in pausa quella che li usa." : undefined}>
                  {(id) => (
                    <select id={id} value={value} onChange={(e) => set(e.target.value)} className={inputClass}>
                      <option value="" disabled>
                        {conti.length ? "Scegli il conto" : "Nessun conto con questo ruolo"}
                      </option>
                      {conti.map((a) => {
                        const occupato = occupatoDa(a);
                        return (
                          <option key={a.id} value={a.id} disabled={!!occupato}>
                            {a.label} · {a.mt5Login}
                            {occupato ? ` · in ${occupato}` : ""}
                          </option>
                        );
                      })}
                    </select>
                  )}
                </Field>
              );
            })}
          </div>

          <div>
            <p className="mb-2 text-sm text-muted">La coppia che userà la fase</p>
            <PairView prop={prop} broker={broker} canWrite={false} compact />
            {(prop && !prop.isConnected) || (broker && !broker.isConnected) ? (
              <p className="mt-2 text-xs text-warn">
                Puoi creare la fase anche con i conti disconnessi, ma per aprire operazioni vanno connessi.{" "}
                <Link to={`/clienti/${group.client.id}`} className="underline">
                  Connettili dal dettaglio cliente
                </Link>
              </p>
            ) : null}
          </div>

          {props.phase === "PHASE_1" && (
            <Field label="Etichetta del ciclo" hint="Facoltativa, per riconoscerlo nello storico: per esempio FundedNext 100k #1.">
              {(id) => <input id={id} value={props.cycleLabel} onChange={(e) => props.setCycleLabel(e.target.value)} className={inputClass} />}
            </Field>
          )}

          <Field label="Simbolo">
            {(id) =>
              !symbolState || !propServer || !brokerServer ? (
                <p id={id} className="text-sm text-muted">
                  Caricamento dei server…
                </p>
              ) : symbolState.both.length ? (
                <>
                  <select id={id} value={props.symbol} onChange={(e) => props.setSymbol(e.target.value)} className={inputClass}>
                    <option value="" disabled>
                      Scegli il simbolo
                    </option>
                    {symbolState.both.map((c) => (
                      <option key={c} value={c}>
                        {c} · {propServer.symbols.find((s) => s.canonical === c)?.brokerSymbol} / {brokerServer.symbols.find((s) => s.canonical === c)?.brokerSymbol}
                      </option>
                    ))}
                  </select>
                  <MissingSymbols state={symbolState} propServer={propServer} brokerServer={brokerServer} serverName={serverName} link={link} compact />
                </>
              ) : (
                <div id={id} className="rounded-card border border-warn/40 bg-warn/10 p-3 text-sm text-ink">
                  <p>Nessun simbolo è mappato su entrambi i server: la fase non può calcolare i lotti delle due gambe.</p>
                  <MissingSymbols state={symbolState} propServer={propServer} brokerServer={brokerServer} serverName={serverName} link={link} />
                </div>
              )
            }
          </Field>
        </>
      )}
    </div>
  );
}

function MissingSymbols({
  state,
  propServer,
  brokerServer,
  serverName,
  link,
  compact = false,
}: {
  state: { both: string[]; onlyProp: string[]; onlyBroker: string[] };
  propServer: BrokerServer;
  brokerServer: BrokerServer;
  serverName: (s: BrokerServer) => string;
  link: (s: BrokerServer, canonical?: string) => string;
  compact?: boolean;
}) {
  const lines: ReactNode[] = [];
  for (const c of state.onlyProp) {
    lines.push(
      <li key={`p-${c}`}>
        {c} è mappato su <span className="text-prop">{serverName(propServer)}</span> ma non su <span className="text-broker">{serverName(brokerServer)}</span>.{" "}
        <Link to={link(brokerServer, c)} className="underline">
          Mappalo sul server broker
        </Link>
      </li>,
    );
  }
  for (const c of state.onlyBroker) {
    lines.push(
      <li key={`b-${c}`}>
        {c} è mappato su <span className="text-broker">{serverName(brokerServer)}</span> ma non su <span className="text-prop">{serverName(propServer)}</span>.{" "}
        <Link to={link(propServer, c)} className="underline">
          Mappalo sul server prop
        </Link>
      </li>,
    );
  }
  if (!state.both.length && !lines.length) {
    lines.push(
      <li key="none">
        Nessun simbolo mappato né su <span className="text-prop">{serverName(propServer)}</span> né su{" "}
        <span className="text-broker">{serverName(brokerServer)}</span>.{" "}
        <Link to={link(propServer)} className="underline">
          Vai alla pagina Server
        </Link>
      </li>,
    );
  }
  if (!lines.length) return null;
  return <ul className={`${compact ? "mt-2 text-xs text-muted" : "mt-2"} space-y-1`}>{lines}</ul>;
}

function NumField({ label, value, onChange, suffix, hint }: { label: string; value: string; onChange: (v: string) => void; suffix?: string; hint?: string }) {
  return (
    <Field label={label} hint={hint}>
      {(id) => (
        <div className="relative">
          <input id={id} inputMode="decimal" value={value} onChange={(e) => onChange(e.target.value)} className={`${inputClass} ${suffix ? "pr-10" : ""}`} />
          {suffix && <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-faint">{suffix}</span>}
        </div>
      )}
    </Field>
  );
}

function Info({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-faint">{label}</dt>
      <dd className="mt-0.5 text-ink">{children}</dd>
    </div>
  );
}

/** Sempre accanto al moltiplicatore: quanto rende la bruciatura e quante operazioni servono. */
